import React, { forwardRef, type ReactNode } from 'react';
import { StyleSheet, View, type TextInput as RNTextInput } from 'react-native';
import {
  TextInput as PaperTextInput,
  type TextInputProps as PaperTextInputProps,
} from 'react-native-paper';
import { Colors } from '../constants/Colors';
import { TextReg } from './StyledText';

type Props = {
  label?: string;
  showLabel?: boolean;
  icon?: ReactNode;
  onIconPress?: () => void;
  onChange?: (value: string) => void | Promise<void>;
  dense?: boolean;
} & Omit<PaperTextInputProps, 'onChangeText' | 'onChange' | 'dense'>;

export const TextInput = forwardRef<RNTextInput, Props>(
  (
    {
      label,
      showLabel = true,
      value,
      onChange,
      icon,
      style,
      disabled,
      autoCapitalize,
      secureTextEntry,
      multiline,
      onIconPress,
      dense,
      error,
      ...otherProps
    }: Props,
    ref,
  ) => {
    const textInputStyle = [
      styles.textInput,
      multiline && styles.multilineInput,
      error && styles.errorInput,
      style,
    ];

    if (showLabel)
      return (
        <View style={styles.container}>
          <TextReg>{label}</TextReg>
          <PaperTextInput
            multiline={multiline}
            ref={ref}
            placeholder={showLabel ? otherProps.placeholder : label}
            value={value}
            onChangeText={onChange}
            disabled={disabled}
            autoCapitalize={autoCapitalize}
            secureTextEntry={secureTextEntry}
            style={textInputStyle}
            theme={{
              colors: { primary: Colors.text },
              roundness: multiline ? 16 : 30,
            }}
            underlineColor="transparent"
            activeUnderlineColor="transparent"
            underlineStyle={{ backgroundColor: 'transparent' }}
            cursorColor={Colors.text}
            textColor={Colors.text}
            selectionColor={Colors.text}
            dense={dense ?? true}
            left={
              icon && (
                <PaperTextInput.Icon icon={() => icon} onPress={onIconPress} />
              )
            }
            {...otherProps}
          />
        </View>
      );

    return (
      <PaperTextInput
        multiline={multiline}
        ref={ref}
        placeholder={showLabel ? otherProps.placeholder : label}
        value={value}
        onChangeText={onChange}
        disabled={disabled}
        autoCapitalize={autoCapitalize}
        secureTextEntry={secureTextEntry}
        style={textInputStyle}
        theme={{
          colors: { primary: Colors.text },
          roundness: multiline ? 16 : 30,
        }}
        underlineColor="transparent"
        activeUnderlineColor="transparent"
        underlineStyle={{ backgroundColor: 'transparent' }}
        cursorColor={Colors.text}
        textColor={Colors.text}
        selectionColor={Colors.text}
        dense={dense ?? true}
        left={
          icon && (
            <PaperTextInput.Icon icon={() => icon} onPress={onIconPress} />
          )
        }
        {...otherProps}
      />
    );
  },
);
TextInput.displayName = 'TextInput';

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  textInput: {
    borderRadius: 30,
    backgroundColor: Colors.opacity05,
    textAlign: 'auto',
  },
  multilineInput: {
    borderRadius: 16,
    paddingTop: 7, // comes with 9px default padding, so this totals to 16px
  },
  errorInput: {
    borderWidth: 1,
    borderColor: Colors.orange0,
  },
});
